import { useCallback, useEffect } from "react";
import { useStore } from "jotai";
import { chipHighlightNodeIdAtom, nodesAtom } from "@/store/editor-v2";
import { parseVarRefId } from "./variableRefs";

type JotaiStore = ReturnType<typeof useStore>;

export interface BoundSourceHighlight {
  highlight: () => void;
  unhighlight: () => void;
}

// Hovering a bound chip lights up the variable's origin node on the canvas —
// same affordance as hovering an option in the picker menu. Only refs that
// resolve to a canvas node participate (secrets and broken refs would
// otherwise ghost the whole flow). Nodes are read from the store at hover
// time, NOT subscribed: nodesAtom updates every frame during a drag and would
// re-render every chip.
function resolveSourceNodeId(
  store: JotaiStore,
  refId: string | null | undefined
): string | null {
  if (!refId) return null;
  const parsed = parseVarRefId(refId);
  if (!parsed) return null;
  return store.get(nodesAtom).some((n) => n.id === parsed.sourceNodeId)
    ? parsed.sourceNodeId
    : null;
}

export function createBoundSourceHighlight(
  store: JotaiStore,
  refId: string | null | undefined
): BoundSourceHighlight {
  return {
    highlight: () => {
      const id = resolveSourceNodeId(store, refId);
      if (id) store.set(chipHighlightNodeIdAtom, id);
    },
    unhighlight: () => {
      const id = resolveSourceNodeId(store, refId);
      if (id)
        store.set(chipHighlightNodeIdAtom, (prev) =>
          prev === id ? null : prev
        );
    },
  };
}

export function useBoundSourceHighlight(
  refId: string | null | undefined
): BoundSourceHighlight {
  const store = useStore();
  const highlight = useCallback(
    () => createBoundSourceHighlight(store, refId).highlight(),
    [store, refId]
  );
  const unhighlight = useCallback(
    () => createBoundSourceHighlight(store, refId).unhighlight(),
    [store, refId]
  );
  // Clearing the binding (or unmounting) mid-hover removes the hovered
  // element, so its mouseleave never fires — release the highlight here.
  useEffect(() => unhighlight, [unhighlight]);
  return { highlight, unhighlight };
}
